import React from 'react';
import styled, {css} from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {RightOverflowGradient} from './helpers/rightOverflowGradient';
import {getCellBoxShadowStyles} from './helpers/tableCellHelpers';
import {
  firstElementPaddingLeft,
  virtualTableHeaderRowHeight,
  virtualTableRowCheckboxWrapperWidth,
  VirtualTableSortableColumnDirectionsEnum,
} from './helpers/tableHelpers';
import {
  getStickyStyles,
  getWidthSettingsStyles,
  SelectedColumnSort,
  SortableTableColumn,
  TableColumn,
  TableColumnSettings,
} from './tableColumn';

/*
 * Types.
 */

export interface TableRowProps {
  columns: ReadonlyArray<TableColumn<unknown>>;
  shouldShowStickyGradient: boolean;
  shouldShowRightGradient: boolean;

  shouldAllowMultiSelect?: boolean;
  shouldRenderEmptyState?: boolean;
  sortableColumns?: ReadonlyArray<SortableTableColumn>;
  sortedColumn?: SelectedColumnSort;

  onSortChange?: HandlerOf<SelectedColumnSort>;
  onMultiSelectChange?: HandlerOf<boolean>;
}

/*
 * Styles.
 */

const StyledTableHeaderRowContainerDiv = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 1;
`;

interface StyledTableHeaderRowDivProps {
  $settings?: TableColumnSettings;
  $shouldShowStickyGradient: boolean;
  $shouldAllowMultiSelect?: boolean;
}
const StyledTableHeaderRowDiv = styled.div<StyledTableHeaderRowDivProps>`
  position: relative;
  display: flex;
  height: ${virtualTableHeaderRowHeight}px;
  background: ${(p) => p.theme.greys.white};
  overflow: hidden;
  margin-bottom: 8px;

  ${(p) => getStickyStyles({isSticky: Boolean(p.$settings?.sticky)})}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth)}
  ${(p) => getCellBoxShadowStyles(p.$shouldShowStickyGradient)}
`;

const StyledTableFirstHeaderCellContainerDiv = styled.div<StyledTableHeaderRowDivProps>`
  display: flex;
  min-height: ${virtualTableHeaderRowHeight}px;
  padding-left: ${firstElementPaddingLeft}px;

  &::before {
    content: '';
    width: 12px;
    height: ${virtualTableHeaderRowHeight}px;
    margin-left: -12px;
    background: ${(p) => p.theme.greys.white};
  }

  ${(p) =>
    getStickyStyles({
      isSticky: Boolean(p.$settings?.sticky),
      stickyOffset: p.$shouldAllowMultiSelect ? virtualTableRowCheckboxWrapperWidth : 0,
    })}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, 0)};
  ${(p) => getCellBoxShadowStyles(p.$shouldShowStickyGradient)}
`;

interface StyledTableHeaderCellDivProps {
  $isFirstChild?: boolean;
  $isSortable: boolean;
  $columnSortedDirection?: VirtualTableSortableColumnDirectionsEnum;
}
const StyledTableHeaderCellDiv = styled.div<StyledTableHeaderCellDivProps>`
  position: relative;
  padding: 12px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
  display: flex;

  ${(p) =>
    p.$isFirstChild &&
    css`
      padding-left: 0;
    `}
  ${(p) =>
    p.$isSortable &&
    css`
      &:hover {
        color: ${p.theme.greys.shade90};
      }
    `}
  ${(p) =>
    p.$columnSortedDirection &&
    css`
      display: inline-flex;
      color: ${p.theme.greys.shade90};
    `}
`;

const StyledTableBorderLineDiv = styled.div`
  position: absolute;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  height: 1px;
  left: 0;
  right: 0;
  bottom: 0;
`;

const StyledArrowAscIcon = styled(Icon)`
  transform: rotate(180deg);
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  flex: 0 0 ${virtualTableRowCheckboxWrapperWidth}px;
  background: ${(p) => p.theme.greys.white};
  height: 40px;

  ${getStickyStyles({isSticky: true})}
`;

const StyledCheckBox = styled(CheckBox)`
  opacity: 0;
  margin-left: 10px;

  ${hoverSelector} {
    opacity: 1;
  }
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const TableHeaderRow = (props: TableRowProps) => {
  const {
    columns,
    sortableColumns,
    sortedColumn,
    shouldShowStickyGradient,
    shouldShowRightGradient,
    shouldAllowMultiSelect,
    shouldRenderEmptyState,
    onSortChange,
    onMultiSelectChange,
  } = props;

  const handleSortChange = (columnId: string) => {
    const sortableColumnDetails = sortableColumns?.find((c) => c.id === columnId);
    // If the column cant be sorted, return early
    if (!sortableColumnDetails || !onSortChange) {
      return;
    }
    // If the column is currently sorted and we only have one sort option, return early.
    if (columnId === sortedColumn?.id && sortableColumnDetails.directions.length === 1) {
      return;
    }

    // If we do not have a currently sorted column or we are changing the sorted column
    if (!sortedColumn || columnId !== sortedColumn.id) {
      onSortChange({
        id: columnId,
        direction: sortableColumnDetails.directions[0],
      });
      return;
    }

    // Check if we need to wrap the selected sort from the options
    const indexOfCurrentSort = sortableColumnDetails.directions.indexOf(sortedColumn.direction);
    if (sortableColumnDetails.directions.length <= indexOfCurrentSort + 1) {
      onSortChange({
        id: columnId,
        direction: sortableColumnDetails.directions[0],
      });
      return;
    }
    onSortChange({
      id: columnId,
      direction: sortableColumnDetails.directions[indexOfCurrentSort + 1],
    });
  };

  return (
    <StyledTableHeaderRowContainerDiv className={buildHoverParentClassName()}>
      {maybeRenderCheckBox(shouldAllowMultiSelect, shouldRenderEmptyState, onMultiSelectChange)}
      {columns.map((column, index) => {
        const sortDirection = columnSortedDirection(column.id, sortedColumn);
        const isSortable = Boolean(sortableColumns?.some((col) => col.id === column.id));
        if (index === 0) {
          return (
            <StyledTableFirstHeaderCellContainerDiv
              key={column.id}
              $settings={column.settings}
              $shouldShowStickyGradient={shouldShowStickyGradient}
              $shouldAllowMultiSelect={shouldAllowMultiSelect}
            >
              <StyledTableHeaderRowDiv $settings={column.settings} $shouldShowStickyGradient={false}>
                <StyledTableHeaderCellDiv
                  $isFirstChild
                  $columnSortedDirection={sortDirection}
                  $isSortable={isSortable}
                  onClick={() => handleSortChange(column.id)}
                >
                  {column.title}
                  {maybeRenderSortedDirectionIcon(sortDirection)}
                  {column.settings?.tooltip && (
                    <StyledTooltipCoordinator render={() => column.settings?.tooltip} placement="top" />
                  )}
                </StyledTableHeaderCellDiv>
                <StyledTableBorderLineDiv />
              </StyledTableHeaderRowDiv>
            </StyledTableFirstHeaderCellContainerDiv>
          );
        }
        return (
          <StyledTableHeaderRowDiv
            key={column.id}
            $settings={column.settings}
            $shouldShowStickyGradient={shouldShowStickyGradient}
          >
            <StyledTableHeaderCellDiv
              $isSortable={isSortable}
              $columnSortedDirection={sortDirection}
              onClick={() => handleSortChange(column.id)}
            >
              {column.title}
              {maybeRenderSortedDirectionIcon(sortDirection)}
              {column.settings?.tooltip && (
                <StyledTooltipCoordinator render={() => column.settings?.tooltip} placement="top" />
              )}
            </StyledTableHeaderCellDiv>
            <StyledTableBorderLineDiv />
          </StyledTableHeaderRowDiv>
        );
      })}
      {shouldShowRightGradient && <RightOverflowGradient />}
    </StyledTableHeaderRowContainerDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderCheckBox(
  shouldAllowMultiSelect?: boolean,
  shouldRenderEmptyState?: boolean,
  onMultiSelectChange?: HandlerOf<boolean>,
) {
  if (!shouldAllowMultiSelect || !onMultiSelectChange) {
    return null;
  }
  return (
    <StyledCheckboxWrapper>
      {!shouldRenderEmptyState && <StyledCheckBox onChange={onMultiSelectChange} />}
    </StyledCheckboxWrapper>
  );
}

const columnSortedDirection = (
  columnId: string,
  sortedColumn?: SelectedColumnSort,
): VirtualTableSortableColumnDirectionsEnum | undefined => {
  if (!sortedColumn || columnId !== sortedColumn.id) {
    return undefined;
  }
  return sortedColumn.direction;
};

const maybeRenderSortedDirectionIcon = (direction?: VirtualTableSortableColumnDirectionsEnum) => {
  switch (direction) {
    case VirtualTableSortableColumnDirectionsEnum.ASCENDING:
      return <StyledArrowAscIcon name="arrowThickSmall" />;
    case VirtualTableSortableColumnDirectionsEnum.DESCENDING:
      return <Icon name="arrowThickSmall" />;
    default:
      return null;
  }
};
